"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.identificationFormRules = exports.identificationFieldMapping = exports.identificationSchema = void 0;
exports.validateIdentificationData = validateIdentificationData;
exports.createIssueDateValidation = createIssueDateValidation;
exports.createExpiryDateValidation = createExpiryDateValidation;
exports.useIdentificationValidation = useIdentificationValidation;
const converters_1 = require("../converters");
const SOCIAL_SECURITY_NUMBER = 'Social Security Number';
const SSN_ERROR_MESSAGE = 'Invalid Social Security Number (SSN).';
const SSN_PATTERN = /^(?!000|666|9\d{2})\d{3}(?!00)\d{2}(?!0000)\d{4}$/;
function isSocialSecurityNumber(primaryId, taxInfo) {
    const idType = primaryId?.type ?? primaryId?.customType;
    const taxIdTypeId = primaryId?.taxIdTypeId ?? primaryId?.tax_id_type_id ?? taxInfo?.tax_id_type_id;
    return idType === SOCIAL_SECURITY_NUMBER || Number(taxIdTypeId) === 1;
}
function validateSocialSecurityNumber(value) {
    if (!value)
        return true;
    if (typeof value !== 'string')
        return SSN_ERROR_MESSAGE;
    const digits = value.replace(/-/g, '');
    return SSN_PATTERN.test(digits) ? true : SSN_ERROR_MESSAGE;
}
exports.identificationSchema = {
    fields: {
        // Tax Information Fields
        'tax_info_details.is_us_citizen': {
            required: true,
        },
        'tax_info_details.has_tax_number': {
            required: true,
        },
        'tax_info_details.tax_number': {
            conditionalRequired: {
                condition: (allValues) => allValues['tax_info_details.has_tax_number'] === true,
                message: 'Tax number is required when "has tax number" is true',
            },
        },
        // Secure Information Fields - these will be validated per item in array
        'secure_info_details.type': {
            required: true,
        },
        'secure_info_details.value': {
        // Optional field
        },
        'secure_info_details.issue_date': {
            dateFormat: {
                format: 'YYYY-MM-DD',
                message: 'Issue date must be a valid date in YYYY-MM-DD format',
            },
            crossFieldValidation: {
                dependentFields: ['secure_info_details.expiry_date'],
                validate: (issueDate, allValues) => {
                    const expiryDate = allValues['secure_info_details.expiry_date'];
                    // If expiry date exists but no issue date is provided, return error
                    if (expiryDate && !issueDate) {
                        return 'Issue date is required when expiry date is provided';
                    }
                    return true;
                },
            },
        },
        'secure_info_details.expiry_date': {
            dateFormat: {
                format: 'YYYY-MM-DD',
                message: 'Expiry date must be a valid date in YYYY-MM-DD format',
            },
        },
        'secure_info_details.country_code': {
        // Optional field
        },
        'secure_info_details.state_code': {
        // Optional field
        },
    },
    messages: {
        'tax_info_details.is_us_citizen': {
            required: 'US citizen status is required',
        },
        'tax_info_details.has_tax_number': {
            required: 'Tax number status is required',
        },
        'tax_info_details.tax_number': {
            conditionalRequired: 'Tax number is required',
        },
        'secure_info_details.type': {
            required: 'Type is required for secure info',
        },
        'secure_info_details.issue_date': {
            dateFormat: 'Issue date must be a valid date in YYYY-MM-DD format',
        },
        'secure_info_details.expiry_date': {
            dateFormat: 'Expiry date must be a valid date in YYYY-MM-DD format',
        },
    },
};
// Field mapping for different naming conventions (if needed)
exports.identificationFieldMapping = {
// Backend -> Frontend mapping (if UI field names differ)
// Example: 'tax_info_details.tax_number': 'taxNumber'
};
// 🚀 Pre-computed validations for performance
exports.identificationFormRules = (0, converters_1.toReactHookForm)(exports.identificationSchema, exports.identificationFieldMapping);
// Custom validation function that handles nested objects properly
function validateIdentificationData(data) {
    const errors = [];
    try {
        // Validate tax info
        const taxInfo = data.tax_info_details;
        if (!taxInfo) {
            errors.push('Tax information is required');
            return { isValid: false, errors };
        }
        if (typeof taxInfo.is_us_citizen !== 'boolean' && typeof taxInfo.is_alien !== 'boolean') {
            errors.push('Citizen status is required');
        }
        if (typeof taxInfo.has_tax_number !== 'boolean') {
            errors.push('Tax number status is required');
        }
        if (taxInfo.has_tax_number === true) {
            if (!taxInfo.tax_number) {
                errors.push('Tax number is required');
            }
            else if (isSocialSecurityNumber(data.primaryId?.[0], taxInfo)) {
                const validationError = validateSocialSecurityNumber(taxInfo.tax_number);
                if (validationError !== true)
                    errors.push(validationError);
            }
        }
        // Validate secure info details
        const secureInfoDetails = data.secure_info_details;
        if (!Array.isArray(secureInfoDetails)) {
            errors.push('Secure info details must be an array');
            return { isValid: false, errors };
        }
        secureInfoDetails.forEach((item, index) => {
            const itemPrefix = `Secure info item ${index + 1}`;
            if (!item.type) {
                errors.push(`${itemPrefix}: Type is required for secure info`);
            }
            // Date validations
            if (item.expiry_date) {
                if (!validateDateFormat(item.expiry_date)) {
                    errors.push(`${item.type || 'Unknown'} - Expiry date must be a valid date in YYYY-MM-DD format`);
                }
            }
            if (item.issue_date) {
                if (!validateDateFormat(item.issue_date)) {
                    errors.push(`${item.type || 'Unknown'} - Issue date must be a valid date in YYYY-MM-DD format`);
                }
            }
            // Cross-field validation: issue_date required when expiry_date is provided
            if (item.expiry_date && !item.issue_date) {
                errors.push(`${item.type || 'Unknown'} - Issue date is required when expiry date is provided`);
            }
            // Date comparison validation: expiry_date must be after issue_date
            if (item.issue_date &&
                item.expiry_date &&
                validateDateFormat(item.issue_date) &&
                validateDateFormat(item.expiry_date)) {
                const issueDateTime = new Date(item.issue_date).getTime();
                const expiryDateTime = new Date(item.expiry_date).getTime();
                if (expiryDateTime <= issueDateTime) {
                    errors.push(`${item.type || 'Unknown'} - Expiry date must be after issue date`);
                }
            }
        });
    }
    catch (error) {
        errors.push('Invalid identification data structure');
    }
    return {
        isValid: errors.length === 0,
        errors,
    };
}
// Helper functions for dynamic field validation (for UI components)
function createIssueDateValidation(registerKey, getValues) {
    return function validateIssueDate(value) {
        const expiryDate = getValues(`${registerKey}.expiry_date`);
        // If expiry date exists but no issue date is provided, return error
        if (expiryDate && !value) {
            return 'Issue date is required when expiry date is provided';
        }
        // Date format validation
        if (value && !validateDateFormat(value)) {
            return 'Issue date must be a valid date in YYYY-MM-DD format';
        }
        // Date comparison validation - issue date must be before expiry date
        if (value && expiryDate && validateDateFormat(value) && validateDateFormat(expiryDate)) {
            const issueDateTime = new Date(value).getTime();
            const expiryDateTime = new Date(expiryDate).getTime();
            if (issueDateTime >= expiryDateTime) {
                return 'Issue date must be before expiry date';
            }
        }
        return true;
    };
}
function createExpiryDateValidation(registerKey, getValues) {
    return function validateExpiryDate(value) {
        const issueDate = getValues(`${registerKey}.issue_date`);
        // Date format validation
        if (value && !validateDateFormat(value)) {
            return 'Expiry date must be a valid date in YYYY-MM-DD format';
        }
        // Date comparison validation - expiry date must be after issue date
        if (value && issueDate && validateDateFormat(value) && validateDateFormat(issueDate)) {
            const issueDateTime = new Date(issueDate).getTime();
            const expiryDateTime = new Date(value).getTime();
            if (expiryDateTime <= issueDateTime) {
                return 'Expiry date must be after issue date';
            }
        }
        return true;
    };
}
// Custom hook that encapsulates all identification validation logic
function useIdentificationValidation(registerKey, getValues, trigger, watch, React) {
    const issueDateValidation = createIssueDateValidation(registerKey, getValues);
    const expiryDateValidation = createExpiryDateValidation(registerKey, getValues);
    // Watch for changes in expiry_date and trigger issue_date validation
    const expiryDate = watch(`${registerKey}.expiry_date`);
    const issueDate = watch(`${registerKey}.issue_date`);
    // Track if fields have been initialized to prevent validation on mount
    const [isInitialized, setIsInitialized] = React.useState(false);
    React.useEffect(() => {
        // Set initialized flag after first render
        setIsInitialized(true);
    }, []);
    React.useEffect(() => {
        // Only trigger validation after initialization and when expiry_date has a value
        if (!isInitialized)
            return;
        // When expiry_date changes, trigger validation on issue_date
        if (expiryDate !== undefined && expiryDate !== '') {
            trigger(`${registerKey}.issue_date`);
        }
    }, [expiryDate, trigger, registerKey, isInitialized]);
    React.useEffect(() => {
        // Only trigger validation after initialization and when issue_date has a value
        if (!isInitialized)
            return;
        // When issue_date changes, trigger validation on both fields
        if (issueDate !== undefined && issueDate !== '') {
            trigger(`${registerKey}.issue_date`); // Clear the "required" error
            if (expiryDate && expiryDate !== '') {
                trigger(`${registerKey}.expiry_date`); // Check date comparison
            }
        }
    }, [issueDate, expiryDate, trigger, registerKey, isInitialized]);
    return {
        issueDateValidation,
        expiryDateValidation,
    };
}
// Helper function for date format validation (from original DTO)
function validateDateFormat(value) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(value))
        return false;
    const date = new Date(value);
    return (date instanceof Date && !isNaN(date.getTime()) && value === date.toISOString().split('T')[0]);
}
